import { Controller } from "@hotwired/stimulus"

/**
 * Document Context Menu controller
 * Provides right-click context menu for document pages with various actions
 */
export default class extends Controller {
  static targets = ["menu", "page"]
  static values = {
    documentToken: String,
    documentUrl: String,
    pageUrl: String,
    reportUrl: String,
    pageIndex: Number
  }

  connect() {
    // Bind event handlers
    this.handleContextMenu = this.handleContextMenu.bind(this)
    this.handleClickOutside = this.handleClickOutside.bind(this)
    this.handleKeyDown = this.handleKeyDown.bind(this)
    
    // Hide menu initially
    if (this.hasMenuTarget) {
      this.menuTarget.style.display = 'none'
    }
  }

  disconnect() {
    this.hideMenu()
  }

  handleContextMenu(event) {
    event.preventDefault()
    event.stopPropagation()
    
    // Store current page data
    const page = event.currentTarget
    this.currentPageIndex = parseInt(page.dataset.pageIndex || 0)
    this.currentPageUrl = page.querySelector('img')?.src
    
    // Show menu at cursor position
    this.showMenu(event.clientX, event.clientY)
  }

  showMenu(x, y) {
    if (!this.hasMenuTarget) return
    
    const menu = this.menuTarget
    menu.style.display = 'block'
    menu.style.position = 'fixed'
    
    // Position menu at cursor, adjusting if it would go off screen
    const menuRect = menu.getBoundingClientRect()
    const maxX = window.innerWidth - menuRect.width - 10
    const maxY = window.innerHeight - menuRect.height - 10
    
    menu.style.left = `${Math.min(x, maxX)}px`
    menu.style.top = `${Math.min(y, maxY)}px`
    menu.style.zIndex = '10000'
    
    // Add event listeners
    document.addEventListener('click', this.handleClickOutside)
    document.addEventListener('contextmenu', this.handleClickOutside)
    document.addEventListener('keydown', this.handleKeyDown)
  }

  hideMenu() {
    if (this.hasMenuTarget) {
      this.menuTarget.style.display = 'none'
    }
    
    // Remove event listeners
    document.removeEventListener('click', this.handleClickOutside)
    document.removeEventListener('contextmenu', this.handleClickOutside)
    document.removeEventListener('keydown', this.handleKeyDown)
  }

  handleClickOutside(event) {
    if (this.hasMenuTarget && !this.menuTarget.contains(event.target)) {
      this.hideMenu()
    }
  }

  handleKeyDown(event) {
    if (event.key === 'Escape') {
      this.hideMenu()
    }
  }

  // Menu actions
  downloadOriginal(event) {
    event.preventDefault()
    this.hideMenu()
    window.location.href = this.documentUrlValue
  }

  downloadReport(event) {
    event.preventDefault()
    this.hideMenu()
    window.location.href = this.reportUrlValue
  }

  copyOriginalUrl(event) {
    event.preventDefault()
    this.hideMenu()
    this.copyToClipboard(this.documentUrlValue)
    this.showToast('Original document URL copied to clipboard')
  }

  copyPageUrl(event) {
    event.preventDefault()
    this.hideMenu()
    if (this.currentPageUrl) {
      this.copyToClipboard(this.currentPageUrl)
      this.showToast('Page image URL copied to clipboard')
    }
  }

  openOriginalInNewTab(event) {
    event.preventDefault()
    this.hideMenu()
    window.open(this.documentUrlValue, '_blank')
  }

  openPageInNewTab(event) {
    event.preventDefault()
    this.hideMenu()
    if (this.currentPageUrl) {
      window.open(this.currentPageUrl, '_blank')
    }
  }

  resetPosition(event) {
    event.preventDefault()
    this.hideMenu()
    
    // Find and call magnifier controller's reset method
    const magnifierController = this.application.getControllerForElementAndIdentifier(
      this.element.closest('[data-controller*="magnifier"]'),
      'magnifier'
    )
    if (magnifierController && magnifierController.resetZoom) {
      magnifierController.resetZoom()
    }
  }

  toggleBoundingBoxes(event) {
    event.preventDefault()
    this.hideMenu()
    
    // Find document-pages controller and toggle overlays
    const docPagesController = this.application.getControllerForElementAndIdentifier(
      this.element.closest('[data-controller*="document-pages"]'),
      'document-pages'
    )
    
    if (docPagesController) {
      // Toggle PDF annotations
      if (docPagesController.hasPdfAnnotationsSwitchTarget) {
        docPagesController.pdfAnnotationsSwitchTarget.checked = !docPagesController.pdfAnnotationsSwitchTarget.checked
        docPagesController.togglePdfAnnotations()
      }
      // Toggle Doctamper overlay
      if (docPagesController.hasDoctamperSwitchTarget) {
        docPagesController.doctamperSwitchTarget.checked = !docPagesController.doctamperSwitchTarget.checked
        docPagesController.toggleDoctamper()
      }
    }
  }

  // Helper methods
  copyToClipboard(text) {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).catch(err => {
        console.error('Failed to copy to clipboard:', err)
        this.fallbackCopyToClipboard(text)
      })
    } else {
      this.fallbackCopyToClipboard(text)
    }
  }

  fallbackCopyToClipboard(text) {
    const textarea = document.createElement('textarea')
    textarea.value = text
    textarea.style.position = 'fixed'
    textarea.style.opacity = '0'
    document.body.appendChild(textarea)
    textarea.select()
    try {
      document.execCommand('copy')
    } catch (err) {
      console.error('Fallback copy failed:', err)
    }
    document.body.removeChild(textarea)
  }

  showToast(message) {
    // Create a simple toast notification
    const toast = document.createElement('div')
    toast.className = 'toast-notification'
    toast.textContent = message
    toast.style.cssText = `
      position: fixed;
      bottom: 20px;
      left: 50%;
      transform: translateX(-50%);
      background: rgba(0, 0, 0, 0.8);
      color: white;
      padding: 12px 24px;
      border-radius: 4px;
      z-index: 10001;
      animation: slideUp 0.3s ease;
    `
    
    document.body.appendChild(toast)
    
    setTimeout(() => {
      toast.style.animation = 'fadeOut 0.3s ease'
      setTimeout(() => {
        document.body.removeChild(toast)
      }, 300)
    }, 2000)
  }
}